import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import portalAxios from "../portalAxios";
import { PortalConsentResponse } from "../contracts/PortalConsentResponse";
import { PortalProfileResponse } from "../contracts/PortalProfileResponse";
import { UpdatePortalProfileRequest } from "../contracts/UpdatePortalProfileRequest";
import { toPortalConsent, toPortalConsents, toPortalProfile } from "../mappers/PortalProfileResponseMapper";
import { PortalConsent, PortalProfile } from "../models/PortalProfile";

const base = (memberId: string): string => `/portal/members/${memberId}`;

/** The shown member's details and language (self or ward). */
export const fetchPortalProfile = createAsyncThunk<PortalProfile, string, { rejectValue: ApiErrorResponse }>(
  "portal/fetchProfile",
  async (memberId, { rejectWithValue }) => {
    try {
      const response = await portalAxios.get<PortalProfileResponse>(`${base(memberId)}/profile`);
      return toPortalProfile(response.data);
    } catch (error) {
      return rejectWithValue(toApiError(error));
    }
  },
);

/** Changes the shown member's language — mail and documents follow (PD-0014). */
export const updatePortalProfile = createAsyncThunk<
  PortalProfile,
  { memberId: string; request: UpdatePortalProfileRequest },
  { rejectValue: ApiErrorResponse }
>("portal/updateProfile", async ({ memberId, request }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.put<PortalProfileResponse>(`${base(memberId)}/profile`, request);
    return toPortalProfile(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** The shown member's consent records, newest first. */
export const fetchPortalConsents = createAsyncThunk<PortalConsent[], string, { rejectValue: ApiErrorResponse }>(
  "portal/fetchConsents",
  async (memberId, { rejectWithValue }) => {
    try {
      const response = await portalAxios.get<PortalConsentResponse[]>(`${base(memberId)}/consents`);
      return toPortalConsents(response.data);
    } catch (error) {
      return rejectWithValue(toApiError(error));
    }
  },
);

/** Withdraws the shown member's marketing consent (a state change; the record stays). */
export const withdrawPortalConsent = createAsyncThunk<
  PortalConsent,
  { memberId: string; consentId: string },
  { rejectValue: ApiErrorResponse }
>("portal/withdrawConsent", async ({ memberId, consentId }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.post<PortalConsentResponse>(
      `${base(memberId)}/consents/${consentId}/withdraw`,
    );
    return toPortalConsent(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** The file name of the export: the member's own is `my-data.json`, a ward's carries their first name. */
export const portalExportFileName = (firstName: string, isSelf: boolean): string => {
  const slug = firstName.trim().toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "");
  return isSelf || !slug ? "my-data.json" : `${slug}-data.json`;
};

/**
 * Downloads the shown member's data export (GDPR Art. 15/20) — the same payload the school's export
 * produces — and offers it as a JSON file.
 */
export const downloadPortalExport = createAsyncThunk<
  void,
  { memberId: string; firstName: string; isSelf: boolean },
  { rejectValue: ApiErrorResponse }
>("portal/downloadExport", async ({ memberId, firstName, isSelf }, { rejectWithValue }) => {
  try {
    const response = await portalAxios.get<Blob>(`${base(memberId)}/export`, { responseType: "blob" });
    const blob = new Blob([response.data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = portalExportFileName(firstName, isSelf);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});
